/**
 * ShootOrder Quote Relay (storefront side)
 * --------------------------------------------------------------------------
 * The "Request a Quote" form on product pages is rendered and submitted by the
 * third-party Storeify Request a Quote app. This script does NOT replace that
 * form. It passively observes the Storeify submission, captures the field
 * values, and delivers them to ShootOrder in one of two modes:
 *
 *   - "relay"  : POST {payload, dom} to a relay endpoint you host (e.g. a
 *                Cloudflare Worker). The relay holds the ShootOrder token and
 *                makes the server-to-server call. Secure, no CORS issue.
 *                Used when `relayUrl` is configured.
 *
 *   - "direct" : map the fields to a ShootOrder contact and POST straight to
 *                app.shootorder.com from the browser. No server to deploy, but
 *                the token is visible in page source and the request depends on
 *                ShootOrder allowing cross-origin (CORS) calls. Used when
 *                `accessToken` is set and `relayUrl` is blank.
 *
 * Config is injected from the theme via `window.ShootOrderRelay`:
 *   {
 *     relayUrl: '',                 // set -> relay mode
 *     shootOrderUrl: 'https://app.shootorder.com/items/contacts/',
 *     accessToken: '',              // set (and relayUrl blank) -> direct mode
 *     match: 'send-request',
 *     debug: false
 *   }
 *
 * This script never calls preventDefault and never blocks Storeify's own
 * request, so it cannot break the existing quote form.
 */
(function () {
  'use strict';

  var cfg = window.ShootOrderRelay || {};
  var RELAY_URL = cfg.relayUrl || '';
  var SHOOTORDER_URL = cfg.shootOrderUrl || 'https://app.shootorder.com/items/contacts/';
  var ACCESS_TOKEN = cfg.accessToken || '';
  var DEBUG = !!cfg.debug;
  var MATCH = cfg.match ? new RegExp(cfg.match, 'i') : /send-request|storeify/i;

  var MODE = RELAY_URL ? 'relay' : (ACCESS_TOKEN ? 'direct' : 'off');

  function log() {
    if (!DEBUG) return;
    try {
      console.log.apply(console, ['[ShootOrderRelay]'].concat([].slice.call(arguments)));
    } catch (e) {}
  }

  // --- First-touch attribution (UTMs etc.) ---------------------------------
  // UTM params only exist in the URL on the campaign landing page. We capture
  // them on the first visit and persist them so they are still available when
  // the shopper later submits the quote form on another page.
  var ATTRIBUTION_KEY = 'so_attribution';
  var UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
  // ShootOrder attribution fields, typically passed via ad URL params
  // (e.g. Google Ads ValueTrack: atr_device={device}, atr_location={loc_physical_ms}).
  var ATR_KEYS = ['atr_location', 'atr_device', 'atr_audience_type'];
  var CLICK_KEYS = ['gclid', 'fbclid', 'msclkid'];

  function nowIso() {
    try { return new Date().toISOString(); } catch (e) { return ''; }
  }

  // Best-effort device classification from the user agent.
  function detectDevice() {
    try {
      var ua = navigator.userAgent || '';
      if (/iPad|Tablet|PlayBook|Silk|(Android(?!.*Mobi))/i.test(ua)) return 'tablet';
      if (/Mobi|Android|iPhone|iPod|IEMobile|BlackBerry|Opera Mini/i.test(ua)) return 'mobile';
      return 'desktop';
    } catch (e) { return ''; }
  }

  function getStoredAttribution() {
    try { return JSON.parse(localStorage.getItem(ATTRIBUTION_KEY)) || null; } catch (e) { return null; }
  }

  function captureAttribution() {
    try {
      var params = new URLSearchParams(location.search || '');
      var current = {};
      var has = false;
      UTM_KEYS.concat(ATR_KEYS, CLICK_KEYS).forEach(function (k) {
        var v = params.get(k);
        if (v) { current[k] = v; has = true; }
      });

      var store = getStoredAttribution() || {};
      // Landing page + referrer + device: recorded once, on the very first visit.
      if (!store.landing_page) {
        store.landing_page = location.href;
        store.referrer = document.referrer || '';
        store.device = detectDevice();
        store.first_ts = nowIso();
      }
      if (has) {
        // First-touch: set the first time UTMs are seen and never overwritten.
        if (!store.first_touch) { store.first_touch = current; store.first_touch_ts = nowIso(); }
        // Last-touch: updated every time UTMs are seen (kept for reference).
        store.last_touch = current;
        store.last_ts = nowIso();
      }
      localStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(store));
      log('attribution stored', store);
    } catch (e) {
      log('attribution capture error', e);
    }
  }

  // Run on every page load so the first campaign visit is recorded.
  captureAttribution();

  if (MODE === 'off') {
    log('No relayUrl and no accessToken configured; integration is inactive.');
    return;
  }

  function clean(s) {
    return (s || '').replace(/\s+/g, ' ').trim();
  }

  // Find the human-readable label for a Storeify input by walking up the DOM.
  function findLabel(el) {
    try {
      if (el.id) {
        var forLabel = document.querySelector('label[for="' + el.id + '"]');
        if (forLabel) return clean(forLabel.textContent);
      }
      var node = el;
      for (var i = 0; i < 4 && node; i++) {
        node = node.parentElement;
        if (!node) break;
        var lab = node.querySelector('label');
        if (lab) return clean(lab.textContent);
      }
    } catch (e) {}
    return el.getAttribute('placeholder') || el.getAttribute('name') || '';
  }

  // Snapshot the Storeify form fields straight from the DOM, with labels.
  // Captured synchronously at submit time so values are read before the app
  // resets the form on success.
  function collectDomFields() {
    var out = {};
    try {
      var inputs = document.querySelectorAll('[name^="storeifyInput_"], [name^="storeify_"]');
      Array.prototype.forEach.call(inputs, function (el) {
        var name = el.getAttribute('name');
        if (!name) return;
        if ((el.type === 'checkbox' || el.type === 'radio') && !el.checked) return;
        out[name] = { value: el.value, label: findLabel(el) };
      });
    } catch (e) {
      log('collectDomFields error', e);
    }
    return out;
  }

  function looksLikeQuote(text) {
    return typeof text === 'string' && text.indexOf('storeifyInput') !== -1;
  }

  // Normalize a request body into a plain object when possible.
  function parseBody(body) {
    if (!body) return null;
    if (typeof body === 'string') {
      try { return { kind: 'json', data: JSON.parse(body), raw: body }; } catch (e) {}
      try {
        var params = new URLSearchParams(body);
        var o = {};
        params.forEach(function (v, k) { o[k] = v; });
        if (Object.keys(o).length) return { kind: 'form', data: o, raw: body };
      } catch (e) {}
      return { kind: 'text', data: body, raw: body };
    }
    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      var fo = {};
      body.forEach(function (v, k) { fo[k] = v; });
      return { kind: 'formdata', data: fo, raw: JSON.stringify(fo) };
    }
    return null;
  }

  function bodyIsQuote(parsed) {
    if (!parsed) return false;
    if (looksLikeQuote(parsed.raw)) return true;
    try { return JSON.stringify(parsed.data).indexOf('storeifyInput') !== -1; } catch (e) { return false; }
  }

  // Map captured Storeify fields -> ShootOrder contact shape.
  // Matching is by the stable Storeify field key first (reliable), then by
  // label text as a fallback. Values are read from the submitted payload first,
  // falling back to the DOM snapshot.
  // Note: this form repurposes first_name as "Company Name" and last_name as
  // "GST Number"; there is no separate person-name field.
  function mapToContact(dom, payload) {
    var domFields = dom || {};
    var body = (payload && typeof payload === 'object') ? payload : {};

    function byName(n) {
      if (body[n] !== undefined && body[n] !== null && body[n] !== '') return String(body[n]);
      if (domFields[n] && domFields[n].value) return domFields[n].value;
      return '';
    }
    function byLabel(re) {
      for (var n in domFields) {
        if (re.test(domFields[n].label || '')) return domFields[n].value || '';
      }
      return '';
    }

    var email = byName('storeifyInput_email') || byLabel(/e-?mail/i);
    var phone = byName('storeifyInput_e42d36041185') || byLabel(/phone|mobile|contact\s*number/i);
    var company = byName('storeifyInput_first_name') || byLabel(/company/i);
    var gst = byName('storeifyInput_last_name') || byLabel(/gst/i);
    var productCode = byName('storeifyInput_9e0a91d4360a') || byLabel(/product\s*code/i);
    var productModel = byName('storeifyInput_af1bbcf06a1b') || byLabel(/product\s*model/i);
    var comments = byName('storeifyInput_c8b515d88e60') || byLabel(/comment|message|note/i);

    // First-touch attribution (falls back to last-touch if no first-touch).
    var attr = getStoredAttribution() || {};
    var utm = attr.first_touch || attr.last_touch || {};

    return {
      contact: {
        full_name: company || 'Quote Request',
        email: email,
        phone: phone,
        product: [productCode, productModel].filter(Boolean).join(' / '),
        reference_link: location.href,
        others: gst,
        lead_status: 'New',
        message: comments,
        Message: comments,
        utm_source: utm.utm_source || '',
        utm_medium: utm.utm_medium || '',
        utm_campaign: utm.utm_campaign || '',
        utm_term: utm.utm_term || '',
        utm_content: utm.utm_content || '',
        atr_location: utm.atr_location || '',
        atr_device: utm.atr_device || attr.device || '',
        atr_audience_type: utm.atr_audience_type || '',
        clid: utm.gclid || utm.msclkid || '',
        fbclid: utm.fbclid || '',
        landing_page: attr.landing_page || '',
        referrer: attr.referrer || ''
      },
      hasContact: !!(email || phone)
    };
  }

  // --- Relay mode delivery --------------------------------------------------
  function deliverRelay(payload, dom, meta) {
    var body;
    try {
      body = JSON.stringify({
        source: 'storeify',
        meta: meta || {},
        shop: (window.Shopify && window.Shopify.shop) || location.hostname,
        page: location.href,
        payload: payload,
        dom: dom,
        attribution: getStoredAttribution()
      });
    } catch (e) {
      log('serialize failed', e);
      return;
    }
    try {
      fetch(RELAY_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: body,
        keepalive: true,
        mode: 'cors',
        credentials: 'omit'
      })
        .then(function (r) { log('relay responded', r.status); })
        .catch(function (e) { log('relay error', e); });
    } catch (e) {
      log('relay forward failed', e);
    }
  }

  // --- Direct mode delivery (browser -> ShootOrder) -------------------------
  function deliverDirect(dom, payload) {
    var mapped = mapToContact(dom, payload);
    if (!mapped.hasContact) {
      log('No email or phone captured; not sending to ShootOrder.', dom);
      return;
    }
    var sep = SHOOTORDER_URL.indexOf('?') >= 0 ? '&' : '?';
    var url = SHOOTORDER_URL + sep + 'access_token=' + encodeURIComponent(ACCESS_TOKEN);
    var body = JSON.stringify(mapped.contact);
    log('Sending to ShootOrder (direct):', mapped.contact);

    try {
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: body,
        mode: 'cors',
        keepalive: true
      })
        .then(function (r) {
          log('ShootOrder responded', r.status);
          return r.text();
        })
        .then(function (t) { log('ShootOrder body', t); })
        .catch(function (e) {
          // Likely CORS / network. Best-effort fire-and-forget so the data
          // still has a chance to land even if we cannot read the response.
          log('Direct CORS request failed, trying no-cors fallback', e);
          try {
            fetch(url, {
              method: 'POST',
              headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
              body: body,
              mode: 'no-cors',
              keepalive: true
            })
              .then(function () { log('no-cors fallback sent (response is opaque, cannot verify)'); })
              .catch(function (e2) { log('no-cors fallback failed', e2); });
          } catch (e2) {
            log('no-cors fallback threw', e2);
          }
        });
    } catch (e) {
      log('direct send threw', e);
    }
  }

  function deliver(payload, dom, meta) {
    if (MODE === 'relay') deliverRelay(payload, dom, meta);
    else deliverDirect(dom, payload);
  }

  // --- Wrap window.fetch ---------------------------------------------------
  if (window.fetch) {
    var origFetch = window.fetch;
    window.fetch = function (input, init) {
      var url = (typeof input === 'string') ? input : (input && input.url) || '';
      var parsed = null;
      var dom = null;
      try {
        if (url && MATCH.test(url)) {
          parsed = parseBody(init && init.body);
          if (bodyIsQuote(parsed)) {
            dom = collectDomFields(); // snapshot now, before any form reset
          } else {
            parsed = null;
          }
        }
      } catch (e) { log('fetch hook error', e); }

      var promise = origFetch.apply(this, arguments);
      if (parsed) {
        promise.then(function (res) {
          if (res && res.ok) {
            log('captured Storeify fetch', url);
            deliver(parsed.data, dom, { url: url, via: 'fetch' });
          }
        }).catch(function () {});
      }
      return promise;
    };
  }

  // --- Wrap XMLHttpRequest --------------------------------------------------
  if (window.XMLHttpRequest && window.XMLHttpRequest.prototype) {
    var proto = window.XMLHttpRequest.prototype;
    var origOpen = proto.open;
    var origSend = proto.send;

    proto.open = function (method, url) {
      this.__soUrl = url;
      return origOpen.apply(this, arguments);
    };

    proto.send = function (body) {
      var self = this;
      try {
        if (self.__soUrl && MATCH.test(self.__soUrl)) {
          var parsed = parseBody(body);
          if (bodyIsQuote(parsed)) {
            var dom = collectDomFields(); // snapshot now, before any form reset
            self.addEventListener('load', function () {
              if (self.status >= 200 && self.status < 300) {
                log('captured Storeify xhr', self.__soUrl);
                deliver(parsed.data, dom, { url: self.__soUrl, via: 'xhr' });
              }
            });
          }
        }
      } catch (e) { log('xhr hook error', e); }
      return origSend.apply(this, arguments);
    };
  }

  log('initialized — mode =', MODE, '| match =', MATCH);
})();
